import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Form, FormGroup, Input, Label, TabPane, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Swal from "sweetalert2";
import bax_agreements_employers from "../../../../assets/pdf/agreements/organization-agreement.pdf";
import bax_agreements_brokersOrGa from "../../../../assets/pdf/agreements/producer-agreement.pdf";
// Add these imports for Terms of Service and Privacy Policy PDFs
import terms_of_service from "../../../../assets/pdf/agreements/producer-agreement.pdf";
import privacy_policy from "../../../../assets/pdf/agreements/producer-agreement.pdf";
import { DASHBOARD_USER_ROLES } from "../../../../constants";
import { acceptAgreements } from "../../../../slices/general/thunk";

const AgreementsStep = ({
  agreementStep,
  toggleVerticalTab,
  activeVerticalTab,
  currentUser,
  onFinishOnboarding,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCheckedAgreement, setIsCheckedAgreement] = useState(false);
  const [loading, setLoading] = useState(false);

  // Modal states for Terms of Service and Privacy Policy
  const [termsModal, setTermsModal] = useState(false);
  const [privacyModal, setPrivacyModal] = useState(false);

  const isCurrentUserEmployer =
    currentUser?.role === DASHBOARD_USER_ROLES.EMPLOYER;

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await dispatch(acceptAgreements());
      console.log(response);
      const res = response.payload;
      if (res && !res.error) {
        onFinishOnboarding();
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong! Please try again",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong! Please try again",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (file, customFileName) => {
    const link = document.createElement("a");
    link.href = file;
    link.setAttribute("download", customFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleTermsModal = () => setTermsModal(!termsModal);
  const togglePrivacyModal = () => setPrivacyModal(!privacyModal);

  const isFormValid = isCheckedAgreement;

  return (
    <TabPane tabId={agreementStep}>
      <div>
        <h5>Agreements</h5>
        <p className="text-muted">
          Please review and agree to the following agreements
        </p>

        {/* New BaxRx disclaimer text */}
        <div className="alert alert-info mb-4">
          <p className="mb-0">
            By using this platform, you are agreeing to the{" "}
            <a
              className="p-0 text-decoration-underline cursor-pointer"
              onClick={toggleTermsModal}
              style={{ verticalAlign: "baseline" }}
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="p-0 text-decoration-underline cursor-pointer"
              onClick={togglePrivacyModal}
              style={{ verticalAlign: "baseline" }}
            >
              Privacy Policy
            </a>{" "}
            of BaxRx Inc., the underlying service provider and billing entity powering this experience.
          </p>
        </div>
      </div>

      <div className="text-center mt-3">
        <iframe
          src={
            isCurrentUserEmployer
              ? bax_agreements_employers
              : bax_agreements_brokersOrGa
          }
          style={{ width: "100%", height: "600px" }}
          title="Agreements Document"
        ></iframe>
      </div>

      <div className="text-center mt-3">
        <Button
          onClick={() => {
            if (isCurrentUserEmployer) {
              handleDownload(
                bax_agreements_employers,
                "BaxRx_Pharmacy_Services_Agreement_Organization.pdf"
              );
            } else {
              handleDownload(
                bax_agreements_brokersOrGa,
                "BaxRx_Pharmacy_Services_Agreement_Organization.pdf"
              );
            }
          }}
          className="btn btn-primary d-flex justify-content-start align-items-center py-0 px-3"
        >
          Download Agreements
          <i className="ri-arrow-right-line ms-1 fs-2"></i>
        </Button>
      </div>

      <div className="mt-4">
        <Form>
          {/* Combined comprehensive checkbox */}
          <FormGroup check className="mt-3">
            <Label check>
              <Input
                type="checkbox"
                className="form-check-input"
                checked={isCheckedAgreement}
                onChange={() => setIsCheckedAgreement(!isCheckedAgreement)}
              />
              By clicking this box to Agree and Continue, Producer hereby agrees and consents to this BaxRx Pharmacy Services Agreement and this Business Associate Agreement. I also acknowledge and agree that by accessing or using this platform, I am entering into a direct agreement with{" "}
              <strong>BaxRx Inc.</strong>, the service provider and technology platform operator. I have read and agree to be bound by the{" "}
              <a
                className="p-0 text-decoration-underline cursor-pointer"
                onClick={toggleTermsModal}
                style={{ verticalAlign: "baseline" }}
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="p-0 text-decoration-underline cursor-pointer"
                onClick={togglePrivacyModal}
                style={{ verticalAlign: "baseline" }}
              >
                Privacy Policy
              </a>{" "}
              of BaxRx Inc. I understand that BaxRx Inc. is the billing entity and may appear on my payment statement, and that this agreement applies regardless of the branding or logo presented on the platform.
            </Label>
          </FormGroup>

          <div className="text-center mt-3">
            <Button
              color="primary"
              className="d-flex align-items-center py-2 px-4"
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </Form>
      </div>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsModal} toggle={toggleTermsModal} size="lg">
        <ModalHeader toggle={toggleTermsModal}>Terms of Service</ModalHeader>
        <ModalBody>
          <iframe
            src={terms_of_service}
            style={{ width: "100%", height: "500px" }}
            title="Terms of Service"
          ></iframe>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => handleDownload(terms_of_service, "BaxRx_Terms_of_Service.pdf")}
          >
            Download
          </Button>
          <Button color="primary" onClick={toggleTermsModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyModal} toggle={togglePrivacyModal} size="lg">
        <ModalHeader toggle={togglePrivacyModal}>Privacy Policy</ModalHeader>
        <ModalBody>
          <iframe
            src={privacy_policy}
            style={{ width: "100%", height: "500px" }}
            title="Privacy Policy"
          ></iframe>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => handleDownload(privacy_policy, "BaxRx_Privacy_Policy.pdf")}
          >
            Download
          </Button>
          <Button color="primary" onClick={togglePrivacyModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </TabPane>
  );
};

export default AgreementsStep;